import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import CTABanner from "@/components/site/CTABanner";

// Values below paraphrase the language used on the source site — no fabricated claims.
const VALUES = [
    {
        title: "Clarity first",
        body: "We believe clear guidance inspires confident choices — so recommendations are explained plainly, with no jargon.",
    },
    {
        title: "Transparency",
        body: "Our service is shaped by transparency and professionalism, with lasting relationships at its core.",
    },
    {
        title: "Tailored to you",
        body: "Every client's situation is unique. We take the time to understand your circumstances before offering any recommendation.",
    },
    {
        title: "Compliant by design",
        body: "Advice that is personalised, transparent and fully compliant — supporting immediate needs while protecting long-term stability.",
    },
];

export default function About() {
    return (
        <>
            <section className="relative pt-24 md:pt-32 pb-16">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                About Acumen
                            </span>
                        </div>
                        <h1
                            data-testid="about-title"
                            className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl tracking-tight text-brand-primary leading-[1.02]"
                        >
                            Advice
                            <br />
                            <em className="italic font-light text-brand-accent">
                                you can trust.
                            </em>
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            Acumen Financial Services Ltd is a mortgage and
                            protection advisory firm, appointed representative
                            of HL Partnership Limited, dedicated to putting
                            clients first. Our service is shaped by transparency,
                            professionalism and lasting relationships.
                        </p>
                    </div>
                </div>
            </section>

            <section className="relative py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6 relative">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1664575600796-ffa828c5cb6e?auto=format&fit=crop&w=1400&q=85"
                                alt="Financial adviser meeting with clients"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:pl-8">
                        <h2 className="font-serif-editorial text-4xl md:text-5xl text-brand-primary leading-[1.05]">
                            Clear guidance.
                            <em className="italic font-light"> Confident choices.</em>
                        </h2>
                        <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                            We believe clear guidance inspires confident choices,
                            whether you're purchasing your first home, expanding a
                            property portfolio, or preparing for future financial
                            goals.
                        </p>
                        <p className="mt-4 text-lg text-brand-muted leading-relaxed">
                            Every client's situation is unique, and we take the time to
                            understand your circumstances, goals, and concerns before
                            offering any recommendations.
                        </p>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {VALUES.map((v) => (
                                <div
                                    key={v.title}
                                    data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <CheckCircle2
                                            size={18}
                                            className="text-brand-accent"
                                        />
                                        <div className="font-medium text-brand-primary">
                                            {v.title}
                                        </div>
                                    </div>
                                    <p className="text-sm text-brand-muted leading-relaxed">
                                        {v.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-12 flex gap-4">
                            <Link
                                to="/contact"
                                data-testid="about-cta-contact"
                                className="bg-brand-primary text-brand-bg px-8 py-4 text-sm font-medium hover:bg-brand-primaryHover transition-colors"
                            >
                                Speak with an adviser
                            </Link>
                            <Link
                                to="/services"
                                data-testid="about-cta-services"
                                className="border border-brand-primary text-brand-primary px-8 py-4 text-sm font-medium hover:bg-brand-primary hover:text-brand-bg transition-colors"
                            >
                                Our services
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <CTABanner />
        </>
    );
}
